/**
 * NotificationsHistoryViewer — full searchable NotificationDispatches log.
 *
 * Mirrors `/v3/notifications/dispatches` 1:1 with optional filters:
 * eventCode, templateCode, recipient substring, outcome, isTestMode,
 * fromUtc / toUtc, top.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type {
  NotifDispatchHistoryFilters,
  NotifDispatchOutcome,
  NotifDispatchRow,
} from '@retail-ops/shared-types';
import { asIso8601 } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { downloadCsv, formatLocal, rowsToCsv } from '@/utils/time';

type DateRange = '24h' | '7d' | '30d' | '90d';

function fromUtcFor(range: DateRange): string {
  const d = new Date();
  if (range === '24h') d.setHours(d.getHours() - 24);
  else if (range === '7d') d.setDate(d.getDate() - 7);
  else if (range === '30d') d.setDate(d.getDate() - 30);
  else d.setDate(d.getDate() - 90);
  return d.toISOString();
}

const OUTCOMES: NotifDispatchOutcome[] = [
  'sent',
  'skipped-optout',
  'skipped-quiet-hours',
  'skipped-test-mode',
  'failed',
  'dead-lettered',
];

export function NotificationsHistoryViewer(): JSX.Element {
  const [searchParams] = useSearchParams();
  const initialId = searchParams.get('id') ?? '';
  const api = useApiClient();
  const [range, setRange] = useState<DateRange>('7d');
  const [eventCode, setEventCode] = useState<string>('');
  const [templateCode, setTemplateCode] = useState<string>('');
  const [recipient, setRecipient] = useState<string>('');
  const [outcome, setOutcome] = useState<NotifDispatchOutcome | ''>('');
  const [rows, setRows] = useState<ReadonlyArray<NotifDispatchRow>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const filters: NotifDispatchHistoryFilters = {
        fromUtc: asIso8601(fromUtcFor(range)),
        top: 500,
        ...(eventCode.trim() ? { eventCode: eventCode.trim() } : {}),
        ...(templateCode.trim() ? { templateCode: templateCode.trim() } : {}),
        ...(recipient.trim() ? { recipient: recipient.trim() } : {}),
        ...(outcome ? { outcome } : {}),
      };
      const resp = await api.retail.notifications.dispatches(filters);
      // Backend returns either { rows } (typed) or { data } (current shape).
      // Accept both until the contract reconciles (see backend
      // NotifDispatchHistoryResponse).
      const data =
        resp?.rows ??
        ((resp as unknown as { data?: ReadonlyArray<NotifDispatchRow> })?.data ?? []);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [api, range, eventCode, templateCode, recipient, outcome]);

  useEffect(() => {
    void load();
  }, [load]);

  // Highlight the row matching ?id=<id> from a deep-link.
  const highlight = useMemo(() => initialId, [initialId]);

  const exportCsv = (): void => {
    if (rows.length === 0) return;
    const csv = rowsToCsv(rows as unknown as ReadonlyArray<Record<string, unknown>>);
    downloadCsv(
      `notifications-history-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
    );
  };

  return (
    <section className="card">
      <header className="card-header">
        <h3>Notifications history</h3>
        <div className="toolbar__spacer" />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void load()}
        >
          Refresh
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </button>
      </header>

      <div className="toolbar">
        <label className="toolbar__field">
          Range
          <select value={range} onChange={(e) => setRange(e.target.value as DateRange)}>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </label>
        <label className="toolbar__field">
          Event
          <input
            type="text"
            placeholder="e.g. cart.committed"
            value={eventCode}
            onChange={(e) => setEventCode(e.target.value)}
          />
        </label>
        <label className="toolbar__field">
          Template
          <input
            type="text"
            value={templateCode}
            onChange={(e) => setTemplateCode(e.target.value)}
          />
        </label>
        <label className="toolbar__field">
          Recipient
          <input
            type="text"
            placeholder="email or upn substring"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        </label>
        <label className="toolbar__field">
          Outcome
          <select
            value={outcome}
            onChange={(e) =>
              setOutcome((e.target.value as NotifDispatchOutcome | '') || '')
            }
          >
            <option value="">Any</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading && <p>Loading…</p>}
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="empty">No dispatches match these filters.</div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>Event</th>
              <th>Template</th>
              <th>Recipients</th>
              <th>Sent</th>
              <th>Skipped</th>
              <th>Failed</th>
              <th>Test?</th>
              <th>Trigger</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className={highlight === r.id ? 'row-highlight' : undefined}
              >
                <td>{formatLocal(r.requestedAt)}</td>
                <td>{r.eventCode}</td>
                <td>{r.templateCode}</td>
                <td>{r.recipientCount}</td>
                <td>{r.sent}</td>
                <td>{r.skipped}</td>
                <td>
                  {r.failed > 0 ? (
                    <span className="pill pill--err">{r.failed}</span>
                  ) : (
                    r.failed
                  )}
                </td>
                <td>
                  {r.isTestMode ? (
                    <span className="pill pill--warn">Test</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {r.triggerCartId
                    ? `cart ${r.triggerCartId.slice(0, 8)}…`
                    : r.triggerMaintActionId
                      ? `maint ${r.triggerMaintActionId.slice(0, 8)}…`
                      : (r.triggerActor ?? '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
